import { create } from 'zustand';
import { OrderEntryTableRowData } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';

/**
 * Catalog search mode state machine.
 *
 * ```
 *                  onCatalogSearch
 *   [Og] ─────────────────────────────► [CatalogSearching]
 *    ▲                                    │   │   │
 *    │             onCatalogSearchResults │   │   │ onCatalogSearchError
 *    │                ┌───────────────────┘   │   └──────────────┐
 *    │                ▼                       │                  ▼
 *    │         [CatalogResults]               │          [CatalogError]
 *    │                │                       │                  │
 *    │                │    onCatalogSearch    │                  │
 *    │                └─────────────────────►─┤                  │
 *    │                                        │                  │
 *    │         [CatalogEmpty] ◄───────────────┘                  │
 *    │                │        (empty results)                   │
 *    │                │                                          │
 *    └────────────────┴──────── onExitCatalogMode ───────────────┘
 * ```
 *
 * - `Og`: Normal order guide filtering mode (default)
 * - `CatalogSearching`: API request in flight
 * - `CatalogResults`: Showing catalog search results
 * - `CatalogEmpty`: Search completed with no results
 * - `CatalogError`: API error occurred
 */
export enum CatalogSearchMode {
  CatalogEmpty = 'catalog_empty',
  CatalogError = 'catalog_error',
  CatalogResults = 'catalog_results',
  CatalogSearching = 'catalog_searching',
  Og = 'og',
}

export enum CatalogSearchErrorCode {
  Network = 'network',
  QueryTooLong = 'query_too_long',
}

interface CatalogSearchState {
  error: Error | null;
  errorCode: CatalogSearchErrorCode | null;
  mode: CatalogSearchMode;
  results: OrderEntryTableRowData[];
  searchTerm: string;
}

interface CatalogSearchActions {
  /**
   * Handle catalog search initiation.
   * Transitions mode to CatalogSearching and stores the search term.
   */
  onCatalogSearch: (term: string) => void;

  /**
   * Handle catalog search error.
   * Transitions mode to CatalogError with error classification.
   */
  onCatalogSearchError: (error: Error, code: CatalogSearchErrorCode) => void;

  /**
   * Handle catalog search results.
   * Transitions mode to CatalogResults or CatalogEmpty based on results.
   */
  onCatalogSearchResults: (results: OrderEntryTableRowData[]) => void;

  /**
   * Exit catalog mode and return to OG filtering.
   * Clears search term, results, and error.
   */
  onExitCatalogMode: () => void;
}

type CatalogSearchStore = CatalogSearchState & CatalogSearchActions;

const initialState: CatalogSearchState = {
  error: null,
  errorCode: null,
  mode: CatalogSearchMode.Og,
  results: [],
  searchTerm: '',
};

/**
 * Selector: Check if currently in any catalog mode (not OG mode).
 */
export const selectIsCatalogMode = (state: CatalogSearchState): boolean =>
  state.mode !== CatalogSearchMode.Og;

/**
 * Selector: Check if catalog search is loading.
 */
export const selectIsLoading = (state: CatalogSearchState): boolean =>
  state.mode === CatalogSearchMode.CatalogSearching;

/**
 * Selector: Check if should show catalog results.
 */
export const selectShowResults = (state: CatalogSearchState): boolean =>
  state.mode === CatalogSearchMode.CatalogResults;

/**
 * Selector: Check if should show empty state.
 */
export const selectShowEmptyState = (state: CatalogSearchState): boolean =>
  state.mode === CatalogSearchMode.CatalogEmpty;

/**
 * Selector: Check if should show error state.
 */
export const selectShowError = (state: CatalogSearchState): boolean =>
  state.mode === CatalogSearchMode.CatalogError;

/**
 * Catalog Search Store for Sales Portal Order Entry.
 *
 * Manages the state machine for catalog search:
 * - Normal OG filtering mode
 * - Catalog search in progress
 * - Catalog search results display
 * - Empty state handling
 * - Error state handling
 */
export const useCatalogSearchStore = create<CatalogSearchStore>((set) => ({
  ...initialState,

  onCatalogSearch: (term: string) => {
    set({
      error: null,
      errorCode: null,
      mode: CatalogSearchMode.CatalogSearching,
      searchTerm: term,
    });
  },

  onCatalogSearchError: (error: Error, code: CatalogSearchErrorCode) => {
    set({
      error,
      errorCode: code,
      mode: CatalogSearchMode.CatalogError,
    });
  },

  onCatalogSearchResults: (results: OrderEntryTableRowData[]) => {
    set({
      error: null,
      mode:
        results.length > 0
          ? CatalogSearchMode.CatalogResults
          : CatalogSearchMode.CatalogEmpty,
      results,
    });
  },

  onExitCatalogMode: () => {
    set(initialState);
  },
}));
